import { CloseOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { TeamIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeams, selectTeamsByPersonID } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageTeamsModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, personID }) => {
	const dispatch = useDispatch();

	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;

	const allTeams = useSelector(selectTeams);
	const personTeams = useSelector((state) => selectTeamsByPersonID(state, personID));
	const { loadingAddMembersTeam, deleteTeamMemberLoading } = useSelector((state) => state.Teams);

	const [newAssociatedTeams, setNewAssociatedTeams] = useState([]);
	const [removedAssociatedTeams, setRemovedAssociatedTeams] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const canManageMembers = useManagePermittedRoles('manageMembers');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;
	const availableTeams = useMemo(() => {
		const existing = personTeams.map((t) => t.teamID) || [];
		const filtered = existing.filter((id) => !removedAssociatedTeams.includes(id));
		const excluded = new Set([...filtered, ...newAssociatedTeams]);

		return allTeams
			.filter((t) => !excluded.has(t.teamID))
			.map((t) => ({
				label: t.name,
				value: t.teamID
			}));
	}, [allTeams, personTeams, newAssociatedTeams, removedAssociatedTeams]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAssociatedTeams([]);
		setRemovedAssociatedTeams([]);
		setHasChanges(false);
		setVisible(false);
	};

	// ✅ Add team
	const handleAddTeam = (teamIDToAdd) => {
		if (removedAssociatedTeams.includes(teamIDToAdd)) {
			const updatedRemovedTeams = removedAssociatedTeams.filter((id) => id !== teamIDToAdd);
			setRemovedAssociatedTeams(updatedRemovedTeams);
			setHasChanges(newAssociatedTeams.length > 0 || updatedRemovedTeams.length > 0);
		} else {
			const updatedNewTeams = [...newAssociatedTeams, teamIDToAdd];
			setNewAssociatedTeams(updatedNewTeams);
			setHasChanges(updatedNewTeams.length > 0 || removedAssociatedTeams.length > 0);
		}
	};

	// ✅ Remove team
	const handleRemoveTeam = (teamIDToRemove) => {
		if (newAssociatedTeams.includes(teamIDToRemove)) {
			const updatedNewTeams = newAssociatedTeams.filter((id) => id !== teamIDToRemove);
			setNewAssociatedTeams(updatedNewTeams);
			setHasChanges(updatedNewTeams.length > 0 || removedAssociatedTeams.length > 0);
		} else {
			const updatedRemovedTeams = [...removedAssociatedTeams, teamIDToRemove];
			setRemovedAssociatedTeams(updatedRemovedTeams);
			setHasChanges(newAssociatedTeams.length > 0 || updatedRemovedTeams.length > 0);
		}
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		const teamsToAdd = newAssociatedTeams.filter((id) => !removedAssociatedTeams.includes(id));
		const teamsToRemove = removedAssociatedTeams.filter((id) => !newAssociatedTeams.includes(id));

		// Add person to new teams
		if (teamsToAdd.length > 0) {
			teamsToAdd.forEach((teamIDToAdd) => {
				dispatch(
					teamsActions.addTeamMember({
						teamID: teamIDToAdd,
						personIDs: [personID]
					})
				);
			});
		}

		// Remove person from teams
		if (teamsToRemove.length > 0) {
			teamsToRemove.forEach((teamIDToRemove) => {
				dispatch(
					teamsActions.deleteTeamMember({
						teamID: teamIDToRemove,
						personIDs: [personID]
					})
				);
			});
		}

		handleCancel();
	};

	const isLoading = loadingAddMembersTeam || deleteTeamMemberLoading;

	return (
		<>
			<Tooltip title={!canManageMembers ? permissionDeniedMessage : "Manage Person's Teams"} placement='top'>
				<span onClick={(e) => canManageMembers && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<UsergroupAddOutlined color='currentColor' />}
							disabled={!canManageMembers}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={isLoading}>
								Cancel
							</Button>
							<Button onClick={handleSubmit} type='primary' loading={isLoading} disabled={!hasChanges}>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Teams
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Add or remove this person from teams.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add Teams</Text>
							<Select
								showSearch
								placeholder='Select Teams'
								style={{ width: '100%' }}
								options={availableTeams}
								value={null}
								onSelect={handleAddTeam}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Current Teams</Text>
							{personTeams.filter((t) => !removedAssociatedTeams.includes(t.teamID)).length === 0 &&
							newAssociatedTeams.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No team memberships yet. Add teams using the dropdown above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{personTeams
										.filter((team) => !removedAssociatedTeams.includes(team.teamID))
										.map((team) => {
											return (
												<Tag
													key={`existing-${team.teamID}`}
													color='cyan'
													style={{
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<TeamIcon color='currentColor' />
														{team.name}
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemoveTeam(team.teamID)}
														/>
													</Flex>
												</Tag>
											);
										})}
									{newAssociatedTeams.map((teamID) => {
										const team = allTeams.find((t) => t.teamID === teamID);
										return (
											<Tag
												key={`new-${teamID}`}
												color='cyan'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<TeamIcon color='currentColor' />
													{team?.name} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveTeam(teamID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageTeamsModal;
